<template>
  <nio-teleport
    to="body"
    ref="container"
    :class="[
      'option-bundle-popup-teleport',
      { 'option-bundle-popup-teleport--pad': isTpad },
    ]"
    :style="{
      zIndex: globalStore.showAllPage ? 101 : 10,
    }"
  >
    <div v-show="popupVisible" class="option-bundle-popup-mask"></div>
    <div
      class="option-bundle-popup-panel"
      :class="{ 'option-bundle-popup-panel--show': panelVisible }"
      :style="panelStyle"
    >
      <div class="option-bundle-popup">
        <div class="option-bundle-popup__header">
          <div class="option-bundle-popup__header-title">
            {{ headerConfig.title || "配置变更确认" }}
          </div>
          <div
            v-if="headerConfig.sub_title"
            class="option-bundle-popup__header-sub-title"
          >
            {{ headerConfig.sub_title }}
          </div>
        </div>

        <div class="option-bundle-popup__body">
          <div
            v-for="(category, categoryIndex) in popupCategoryList"
            :key="`${category.title || ''}-${categoryIndex}`"
            class="option-bundle-popup__category"
            :class="{
              'option-bundle-popup__category--first': categoryIndex === 0,
            }"
          >
            <div
              v-if="categoryIndex !== 0"
              class="option-bundle-popup__category-title"
            >
              {{ category.title }}
            </div>
            <div
              v-if="categoryIndex !== 0 && category.sub_title"
              class="option-bundle-popup__category-sub-title"
            >
              {{ category.sub_title }}
            </div>
            <div
              v-for="(group, groupIndex) in category.group_list || []"
              :key="`${group.title || ''}-${groupIndex}`"
              class="option-bundle-popup__group"
            >
              <div
                v-if="group.title && (group.option_list?.length || 0) > 1"
                class="option-bundle-popup__group-title"
              >
                {{ group.title }}
              </div>
              <div class="option-bundle-popup__group-options">
                <option-item
                  v-for="(option, optionIndex) in getGroupOptionList(group)"
                  :key="`${option.feature_code || ''}-${
                    option.code || ''
                  }-${optionIndex}`"
                  :option="option"
                  :selectable="category.type === 'select'"
                  @select="handleOptionSelect(option)"
                  @viewDetail="handleOptionDetailView(option)"
                />
                <div
                  v-if="!isGroupExpanded(group)"
                  class="option-bundle-popup__expand"
                  @click="toggleGroupExpand(group)"
                >
                  <span>展开全部</span>
                  <nio-icon type="down" />
                </div>
              </div>
            </div>
            <div
              v-if="category.bottom_desc"
              class="option-bundle-popup__category-bottom-desc"
            >
              {{ category.bottom_desc }}
            </div>
          </div>
        </div>

        <div class="option-bundle-popup__footer">
          <div class="option-bundle-popup__price">
            <div
              v-if="
                priceDifferenceValue !== undefined &&
                popupConfig.price_difference_desc
              "
              class="option-bundle-popup__price-desc"
            >
              {{ popupConfig.price_difference_desc }}
            </div>
            <div
              v-if="priceDifferenceValue !== undefined"
              class="option-bundle-popup__price-value"
            >
              <span
                v-if="priceDifferencePrefix"
                class="option-bundle-popup__price-sign"
              >
                {{ priceDifferencePrefix }}
              </span>
              <span class="option-bundle-popup__price-symbol">¥</span>
              <span class="option-bundle-popup__price-text">
                {{ priceDifferenceValue }}
              </span>
            </div>
          </div>
          <div class="option-bundle-popup__buttons">
            <nio-button
              v-for="(button, buttonIndex) in popupConfig.button_list || []"
              :key="`${button.name || ''}-${button.type || ''}-${buttonIndex}`"
              :hover="button.type === RuleButtonType.Add"
              :disable="isLoading"
              @click="handleButtonClick(button)"
            >
              {{ button.name }}
            </nio-button>
          </div>
        </div>

        <!-- <div v-if="isLoading" class="option-bundle-popup__loading">
          <nio-loading text="更新中" />
        </div> -->
      </div>
    </div>
  </nio-teleport>
</template>

<script setup>
import { isTpad } from "@common/utils";
import { RuleButtonType } from "config-request/src/interfaces/rule-check";
import { useFeatureListStore, useGlobalStore } from "shared";

import OptionItem from "./option-item.vue";
import { useOptionListExpand } from "./composables/useOptionListExpand";
import { usePopupConfig } from "./composables/usePopupConfig";

import "./index.scss";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  lastRuleCheckContext: {
    type: Object,
    default: () => ({ request: null, response: null }),
    required: true,
  },
  enablePadOptionDetail: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:visible", "cancel", "confirm"]);

const popupVisible = computed({
  get: () => props.visible,
  set: (newValue) => emit("update:visible", newValue),
});
const container = ref();
const panelVisible = ref(false);
const sdkHeaderHeight = computed(
  () =>
    document.getElementById("nio-config-header")?.firstChild?.clientHeight || 64
);
const panelStyle = computed(() =>
  isTpad
    ? {
        paddingTop: "20px",
      }
    : {
        paddingTop: `${sdkHeaderHeight.value}px`,
      }
);

const globalStore = useGlobalStore();
const featureListStore = useFeatureListStore();
const { isLoading, resultOptionList, popupConfig, toggleOptionSelect } =
  usePopupConfig(props.lastRuleCheckContext);

const popupCategoryList = computed(() => popupConfig.value.category_list || []);
const headerConfig = computed(() => popupCategoryList.value[0] || {});

const priceDifferenceValue = computed(() => {
  if (popupConfig.value.price_difference === undefined) {
    return undefined;
  }
  return Math.abs(popupConfig.value.price_difference || 0).toLocaleString("en");
});

const priceDifferencePrefix = computed(() => {
  if (popupConfig.value.price_difference === undefined) {
    return "";
  }
  if (popupConfig.value.price_difference > 0) {
    return "+";
  }
  if (popupConfig.value.price_difference < 0) {
    return "-";
  }
  return "";
});

const handleButtonClick = (button) => {
  switch (button?.type) {
    case RuleButtonType.Add:
      emit("confirm", resultOptionList.value);
      break;
    case RuleButtonType.Cancel:
      emit("update:visible", false);
      emit("cancel");
      break;
    default:
      emit("confirm", resultOptionList.value);
      break;
  }
};

const resolveOptionItem = (option) => {
  const optionFromStore = featureListStore.getOptionCodeItem(option?.code);
  return {
    ...optionFromStore,
    ...option,
  };
};

const handleOptionDetailView = (option) => {
  handleOptionDetailViewAsync(option);
};

let dialogDetailCreator = null;
let optionDetailCreator = null;

const loadDialogDetail = async () => {
  if (dialogDetailCreator) return dialogDetailCreator;
  const module = await import("@cmp/DialogDetail");
  dialogDetailCreator = module.default;
  return dialogDetailCreator;
};

const loadOptionDetail = async () => {
  if (optionDetailCreator) return optionDetailCreator;
  const module = await import("@cmp/OptionDetail");
  optionDetailCreator = module.default;
  return optionDetailCreator;
};

const handleOptionDetailViewAsync = async (option) => {
  if (!option?.code) return;
  const resolvedOption = resolveOptionItem(option);
  if (!resolvedOption?.detail_desc_url && !resolvedOption?.have_detail) return;

  const shouldUseOptionDetail = !isTpad || props.enablePadOptionDetail;
  if (
    resolvedOption.option_detail_style_type === "support_material" &&
    shouldUseOptionDetail
  ) {
    const OptionDetail = await loadOptionDetail();
    OptionDetail({
      optionData: resolvedOption,
      noButton: true,
      withMask: false,
      style: {
        zIndex: globalStore.showAllPage ? 101 : 10,
      },
    });
    return;
  }

  const DialogDetail = await loadDialogDetail();
  DialogDetail(resolvedOption, undefined, {
    wrapperStyle: {
      zIndex: globalStore.showAllPage ? 101 : 10,
    },
  });
};

const handleOptionSelect = (option) => {
  toggleOptionSelect(option, true);
};

const { isGroupExpanded, getGroupOptionList, toggleGroupExpand } =
  useOptionListExpand();

const syncContainerVisible = (visible) => {
  const teleportEl = container.value?.$el;
  if (!teleportEl) return;
  if (visible) {
    teleportEl.style.width = "100vw";
    return;
  }
  setTimeout(() => {
    teleportEl.removeAttribute("style");
  }, 350);
};

const openPanel = () => {
  syncContainerVisible(true);
  panelVisible.value = false;
  setTimeout(() => {
    panelVisible.value = true;
  }, 16);
};

const closePanel = () => {
  panelVisible.value = false;
  syncContainerVisible(false);
};

watch(popupVisible, (visible) => {
  if (visible) {
    openPanel();
    return;
  }
  closePanel();
});

onMounted(() => {
  if (popupVisible.value) {
    openPanel();
  }
});
</script>
